import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
    FaTasks, 
    FaCheckCircle, 
    FaClock, 
    FaExclamationTriangle,
    FaSpinner,
    FaChartLine,
    FaCalendarAlt,
    FaFilter,
    FaDownload,
    FaSync,
    FaChartPie,
    FaChartBar,
    FaEye,
    FaArrowRight,
    FaPlus,
    FaExclamationCircle
} from 'react-icons/fa';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
    LineChart,
    Line
} from 'recharts';
import toast from 'react-hot-toast';

// Loading Skeleton Component (custom implementation)
const LoadingSkeleton = ({ width = '100%', height = '20px', className = '' }) => (
    <div 
        className={`animate-pulse bg-gray-200 rounded ${className}`}
        style={{ width, height }}
    />
);

// Custom Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Dashboard Error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center h-96 p-8">
                    <FaExclamationCircle className="text-red-500 text-6xl mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h2>
                    <p className="text-gray-600 mb-4">Failed to load dashboard. Please try refreshing.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                        Refresh Page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

// Dashboard Skeleton Component
const DashboardSkeleton = () => (
    <div className="page-content">
        <div className="sticky top-0 bg-white z-10 pb-4 mb-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <LoadingSkeleton width={200} height={32} />
                    <LoadingSkeleton width={250} height={20} className="mt-2" />
                </div>
                <div className="flex gap-3">
                    <LoadingSkeleton width={100} height={40} />
                    <LoadingSkeleton width={100} height={40} />
                    <LoadingSkeleton width={120} height={40} />
                </div>
            </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-md p-6">
                    <LoadingSkeleton height={60} />
                </div>
            ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-md p-6">
                <LoadingSkeleton height={400} />
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
                <LoadingSkeleton height={400} />
            </div>
        </div>
    </div>
);

// Custom Hook for Dashboard Data
const useDashboardData = (dateRange) => {
    const [data, setData] = useState({
        stats: null,
        recentTasks: [],
        upcomingTasks: [],
        weeklyData: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const [statsRes, recentRes, upcomingRes, weeklyRes] = await Promise.allSettled([
                axios.get('/api/tasks/stats'),
                axios.get('/api/tasks/recent?limit=10'),
                axios.get('/api/tasks/upcoming?days=7'),
                axios.get(`/api/tasks/weekly-stats?range=${dateRange}`)
            ]);

            // Handle each response individually
            const newData = {
                stats: statsRes.status === 'fulfilled' ? statsRes.value.data.stats : { total: 0, completed: 0, pending: 0, overdue: 0, inProgress: 0 },
                recentTasks: recentRes.status === 'fulfilled' ? recentRes.value.data.tasks || [] : [],
                upcomingTasks: upcomingRes.status === 'fulfilled' ? upcomingRes.value.data.tasks || [] : [],
                weeklyData: weeklyRes.status === 'fulfilled' ? weeklyRes.value.data.weeklyStats || [] : []
            };

            setData(newData);

            // Show warning for failed requests
            const failedRequests = [statsRes, recentRes, upcomingRes, weeklyRes].filter(res => res.status === 'rejected');
            if (failedRequests.length > 0) {
                toast.error(`${failedRequests.length} data source(s) failed to load`);
            }
        } catch (err) {
            console.error('Dashboard data fetch error:', err);
            setError('Failed to load dashboard data. Please check your connection.');
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { ...data, loading, error, refetch: fetchData };
};

const Dashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [dateRange, setDateRange] = useState('week');
    const [selectedChart, setSelectedChart] = useState('area');
    const [showFilters, setShowFilters] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPriority, setFilterPriority] = useState('all');
    const [refreshing, setRefreshing] = useState(false);
    
    const { stats, recentTasks, upcomingTasks, weeklyData, loading, error, refetch } = useDashboardData(dateRange);

    const handleRefresh = async () => {
        setRefreshing(true);
        await refetch();
        toast.success('Dashboard refreshed successfully');
        setTimeout(() => setRefreshing(false), 500);
    };

    const handleExport = useCallback(() => {
        try {
            const exportData = {
                stats,
                recentTasks,
                upcomingTasks,
                weeklyData,
                exportedAt: new Date().toISOString(),
                user: user?.email,
                filters: { filterStatus, filterPriority, dateRange }
            };
            
            const dataStr = JSON.stringify(exportData, null, 2);
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dashboard-data-${new Date().toISOString().slice(0,19)}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('Data exported successfully');
        } catch (err) {
            console.error('Export error:', err);
            toast.error('Failed to export data');
        }
    }, [stats, recentTasks, upcomingTasks, weeklyData, user, filterStatus, filterPriority, dateRange]);

    const filteredRecentTasks = useMemo(() => {
        return recentTasks.filter(task => {
            if (filterStatus !== 'all' && task.status !== filterStatus) return false;
            if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
            return true;
        });
    }, [recentTasks, filterStatus, filterPriority]);

    const statCards = useMemo(() => [
        { 
            title: 'Total Tasks', 
            value: stats?.total || 0, 
            icon: FaTasks, 
            color: 'from-blue-500 to-blue-600',
            textColor: 'text-blue-600',
            bgGradient: 'bg-gradient-to-br from-blue-50 to-blue-100',
            trend: stats?.totalTrend || '+0%',
            trendUp: true,
            onClick: () => navigate('/tasks')
        },
        { 
            title: 'Completed', 
            value: stats?.completed || 0, 
            icon: FaCheckCircle, 
            color: 'from-green-500 to-green-600',
            textColor: 'text-green-600',
            bgGradient: 'bg-gradient-to-br from-green-50 to-green-100',
            trend: stats?.completedTrend || '+0%',
            trendUp: true,
            onClick: () => navigate('/tasks?status=completed')
        },
        { 
            title: 'Pending', 
            value: stats?.pending || 0, 
            icon: FaClock, 
            color: 'from-yellow-500 to-yellow-600',
            textColor: 'text-yellow-600',
            bgGradient: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
            trend: stats?.pendingTrend || '+0%',
            trendUp: false,
            onClick: () => navigate('/tasks?status=pending')
        },
        { 
            title: 'Overdue', 
            value: stats?.overdue || 0, 
            icon: FaExclamationTriangle, 
            color: 'from-red-500 to-red-600',
            textColor: 'text-red-600',
            bgGradient: 'bg-gradient-to-br from-red-50 to-red-100',
            trend: stats?.overdueTrend || '+0%',
            trendUp: true,
            onClick: () => navigate('/tasks?status=overdue')
        }
    ], [stats, navigate]);

    const completionRate = useMemo(() => 
        stats?.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0,
        [stats]
    );

    const pieData = useMemo(() => [
        { name: 'Completed', value: stats?.completed || 0, color: '#10B981' },
        { name: 'In Progress', value: stats?.inProgress || 0, color: '#3B82F6' },
        { name: 'Pending', value: stats?.pending || 0, color: '#F59E0B' }
    ].filter(item => item.value > 0), [stats]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-4 shadow-xl rounded-lg border border-gray-100">
                    <p className="font-semibold text-gray-800 mb-2">{label}</p>
                    {payload.map((p, index) => (
                        <p key={index} className="text-sm flex items-center gap-2 mb-1" style={{ color: p.color }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: p.color }}></span>
                            {p.name}: <span className="font-bold">{p.value}</span> tasks
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const renderTrendChart = useCallback(() => {
        if (!weeklyData || weeklyData.length === 0) {
            return (
                <div className="flex justify-center items-center h-80 text-gray-500">
                    <FaSpinner className="animate-spin mr-2" />
                    <p>No trend data available</p>
                </div>
            );
        }

        const commonProps = {
            data: weeklyData,
            margin: { top: 10, right: 30, left: 0, bottom: 0 }
        };

        const chartComponents = {
            bar: (
                <BarChart {...commonProps}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="completed" fill="#10B981" name="Completed" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="created" fill="#3B82F6" name="Created" radius={[4, 4, 0, 0]} />
                </BarChart>
            ),
            line: (
                <LineChart {...commonProps}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="completed" stroke="#10B981" strokeWidth={2} name="Completed" dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="created" stroke="#3B82F6" strokeWidth={2} name="Created" dot={{ r: 4 }} />
                </LineChart>
            ),
            area: (
                <AreaChart {...commonProps}>
                    <defs>
                        <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area type="monotone" dataKey="completed" stackId="1" stroke="#10B981" fill="url(#colorCompleted)" name="Completed" />
                    <Area type="monotone" dataKey="created" stackId="1" stroke="#3B82F6" fill="url(#colorCreated)" name="Created" />
                </AreaChart>
            )
        };

        return chartComponents[selectedChart] || chartComponents.area;
    }, [weeklyData, selectedChart]);

    // Helper function for status button classes
    const getStatusButtonClass = (status, isActive) => {
        if (!isActive) {
            return 'bg-gray-100 text-gray-600 hover:bg-gray-200';
        }
        
        switch(status) {
            case 'Pending':
                return 'bg-yellow-500 text-white';
            case 'In Progress':
                return 'bg-blue-500 text-white';
            case 'Completed':
                return 'bg-green-500 text-white';
            case 'Overdue':
                return 'bg-red-500 text-white';
            default:
                return 'bg-indigo-500 text-white';
        }
    };

    if (loading) {
        return <DashboardSkeleton />;
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-96 p-8">
                <FaExclamationCircle className="text-red-500 text-6xl mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Dashboard</h2>
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                    onClick={handleRefresh}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                >
                    <FaSync className={refreshing ? 'animate-spin' : ''} />
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <div className="page-content">
                {/* Sticky Header */}
                <div className="sticky top-0 bg-white z-10 pb-4 mb-6 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
                            <p className="text-gray-500 text-sm mt-1">
                                Welcome back, {user?.name || user?.email || 'User'}!
                            </p>
                        </div>
                        <div className="flex gap-3 flex-wrap">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm ${
                                    showFilters ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                aria-label="Toggle filters"
                            >
                                <FaFilter /> Filters
                            </button>
                            <button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm"
                                aria-label="Refresh dashboard"
                            >
                                <FaSync className={refreshing ? 'animate-spin' : ''} />
                                Refresh
                            </button>
                            <button
                                onClick={handleExport}
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                                aria-label="Export data"
                            >
                                <FaDownload />
                                Export Data
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Task Status</label>
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                >
                                    <option value="all">All Status</option>
                                    <option value="Pending">Pending</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Overdue">Overdue</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                                <select
                                    value={filterPriority}
                                    onChange={(e) => setFilterPriority(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                >
                                    <option value="all">All Priorities</option>
                                    <option value="High">High</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Low">Low</option>
                                </select>
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={() => {
                                        setFilterStatus('all');
                                        setFilterPriority('all');
                                    }}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                >
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    {statCards.map((stat, index) => (
                        <div
                            key={index}
                            onClick={stat.onClick}
                            className="bg-white rounded-xl shadow-md p-6 cursor-pointer group hover:shadow-lg transition-all duration-200 hover:-translate-y-1 border border-gray-100"
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => e.key === 'Enter' && stat.onClick()}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium">{stat.title}</p>
                                    <p className="text-3xl font-bold mt-2 text-gray-800">{stat.value}</p>
                                    <div className="flex items-center gap-1 mt-2">
                                        <span className={`text-xs ${stat.trendUp ? 'text-green-500' : 'text-red-500'} font-medium`}>
                                            {stat.trend}
                                        </span>
                                        <span className="text-xs text-gray-400">vs last month</span>
                                    </div>
                                </div>
                                <div className={`bg-gradient-to-br ${stat.color} p-3 rounded-full text-white shadow-lg group-hover:scale-110 transition-transform duration-200`}>
                                    <stat.icon size={24} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Welcome Banner */}
                <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 rounded-xl shadow-lg p-6 text-white mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex-1">
                            <h2 className="text-xl font-bold mb-2">Task Summary</h2>
                            <p className="opacity-90">
                                You have completed {stats?.completed || 0} out of {stats?.total || 0} tasks
                            </p>
                            <div className="mt-3 w-full max-w-md bg-white/20 rounded-full h-2 overflow-hidden">
                                <div 
                                    className="bg-white rounded-full h-2 transition-all duration-500"
                                    style={{ width: `${completionRate}%` }}
                                />
                            </div>
                            <p className="text-sm mt-2 opacity-90">{completionRate}% Complete</p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => navigate('/create-task')}
                                className="px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 font-medium"
                            >
                                <FaPlus /> Create New Task
                            </button>
                            <button
                                onClick={() => navigate('/tasks')}
                                className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2"
                            >
                                View All Tasks <FaArrowRight />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Pie Chart */}
                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <FaChartPie className="text-indigo-500" />
                                Task Status Distribution
                            </h2>
                            <div className="flex gap-2">
                                {['week', 'month', 'year'].map((range) => (
                                    <button
                                        key={range}
                                        onClick={() => setDateRange(range)}
                                        className={`px-3 py-1 text-sm rounded-md transition-colors capitalize ${
                                            dateRange === range 
                                                ? 'bg-indigo-500 text-white' 
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {range}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {pieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={320}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend verticalAlign="bottom" height={36} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex justify-center items-center h-80 text-gray-500">
                                <p>No data available</p>
                            </div>
                        )}
                    </div>

                    {/* Trend Chart */}
                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <FaChartBar className="text-indigo-500" />
                                Task Trend ({dateRange === 'week' ? 'Last 7 Days' : dateRange === 'month' ? 'Last 4 Weeks' : 'Last 12 Months'})
                            </h2>
                            <div className="flex gap-2">
                                {['area', 'bar', 'line'].map((type) => (
                                    <button
                                        key={type}
                                        onClick={() => setSelectedChart(type)}
                                        className={`px-3 py-1 text-sm rounded-md transition-colors capitalize ${
                                            selectedChart === type 
                                                ? 'bg-indigo-500 text-white' 
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={320}>
                            {renderTrendChart()}
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Upcoming and Recent Tasks */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Upcoming Tasks */}
                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <FaCalendarAlt className="text-indigo-500" />
                                Upcoming Tasks (Next 7 Days)
                                <span className="text-sm font-normal text-gray-500">
                                    ({upcomingTasks.length} tasks)
                                </span>
                            </h2>
                            <button 
                                onClick={() => navigate('/upcoming')}
                                className="text-sm text-indigo-500 hover:text-indigo-600 flex items-center gap-1"
                            >
                                View All <FaArrowRight size={12} />
                            </button>
                        </div>
                        {upcomingTasks.length > 0 ? (
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {upcomingTasks.slice(0, 5).map((task) => {
                                    const dueDate = new Date(task.due_date);
                                    const today = new Date();
                                    const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                                    
                                    return (
                                        <div
                                            key={task.task_id || task.id}
                                            onClick={() => navigate(`/task/${task.task_id || task.id}`)}
                                            className="p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-all duration-200 hover:shadow-md group"
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-800 group-hover:text-indigo-600 transition-colors">
                                                        {task.title}
                                                    </p>
                                                    <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                                                        <FaCalendarAlt size={12} />
                                                        Due: {dueDate.toLocaleDateString()}
                                                        {daysUntil <= 2 && daysUntil >= 0 && (
                                                            <span className="text-red-500 font-semibold text-xs flex items-center gap-1">
                                                                <FaExclamationTriangle size={10} />
                                                                Due soon!
                                                            </span>
                                                        )}
                                                        {daysUntil < 0 && (
                                                            <span className="text-red-500 font-semibold text-xs flex items-center gap-1">
                                                                <FaExclamationTriangle size={10} />
                                                                Overdue!
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                                        task.priority === 'High' ? 'bg-red-100 text-red-700' :
                                                        task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                                        'bg-green-100 text-green-700'
                                                    }`}>
                                                        {task.priority || 'Medium'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {upcomingTasks.length > 5 && (
                                    <button
                                        onClick={() => navigate('/upcoming')}
                                        className="w-full text-center text-sm text-indigo-500 hover:text-indigo-600 py-2"
                                    >
                                        View {upcomingTasks.length - 5} more tasks...
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <FaSpinner className="mx-auto text-4xl mb-2 opacity-30" />
                                <p>No upcoming tasks! 🎉</p>
                            </div>
                        )}
                    </div>

                    {/* Recent Tasks */}
                    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <FaTasks className="text-indigo-500" />
                                Recent Tasks
                                <span className="text-sm font-normal text-gray-500">
                                    ({filteredRecentTasks.length} tasks)
                                </span>
                            </h2>
                            <button 
                                onClick={() => navigate('/tasks')}
                                className="text-sm text-indigo-500 hover:text-indigo-600 flex items-center gap-1"
                            >
                                View All <FaArrowRight size={12} />
                            </button>
                        </div>
                        
                        {/* Filter status badges */}
                        <div className="flex gap-2 mb-4 flex-wrap">
                            {['all', 'Pending', 'In Progress', 'Completed', 'Overdue'].map(status => (
                                <button
                                    key={status}
                                    onClick={() => setFilterStatus(status === 'all' ? 'all' : status)}
                                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                        filterStatus === (status === 'all' ? 'all' : status)
                                            ? getStatusButtonClass(status, true)
                                            : getStatusButtonClass(status, false)
                                    }`}
                                >
                                    {status === 'all' ? 'All' : status}
                                </button>
                            ))}
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Task
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Due Date
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Priority
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Action
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredRecentTasks.slice(0, 5).map((task) => (
                                        <tr
                                            key={task.task_id || task.id}
                                            className="cursor-pointer hover:bg-gray-50 transition-colors group"
                                            onClick={() => navigate(`/task/${task.task_id || task.id}`)}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-medium text-gray-900 group-hover:text-indigo-600">
                                                    {task.title}
                                                </div>
                                                {task.description && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {task.description.substring(0, 40)}...
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No date'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                                    task.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                                    task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                                                    task.status === 'Overdue' ? 'bg-red-100 text-red-700' :
                                                    'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                    {task.status || 'Pending'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                                    task.priority === 'High' ? 'bg-red-100 text-red-700' :
                                                    task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-green-100 text-green-700'
                                                }`}>
                                                    {task.priority || 'Medium'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/task/${task.task_id || task.id}`);
                                                    }}
                                                    className="text-indigo-500 hover:text-indigo-700"
                                                    aria-label="View task details"
                                                >
                                                    <FaEye size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredRecentTasks.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                                No recent tasks found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default Dashboard;